import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import PrimaryButton from '../components/PrimaryButton';
import Alert from '@mui/material/Alert';

function AcceptInvite() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [invite, setInvite] = useState(null);
    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [touched, setTouched] = useState({ password: false, confirmPassword: false });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setError('Invalid invite link.');
            return;
        }
        (async () => {
            try {
                const res = await api.get(`/admin/api/admin-users/invite-info?token=${encodeURIComponent(token)}`);
                setInvite(res.data);
            } catch (err) {
                setError('Invalid or expired invite link.');
            }
        })();
    }, [token]);

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
    const handleBlur = field => setTouched(t => ({ ...t, [field]: true }));

    const valid = form.password.length >= 8 && form.password === form.confirmPassword;

    const handleSubmit = async e => {
        e.preventDefault();
        setTouched({ password: true, confirmPassword: true });
        if (!valid) return;
        setSubmitting(true);
        setError('');
        try {
            await api.post('/admin/api/admin-users/accept-invite', { token, password: form.password });
            // Clear any existing auth and redirect to login page
            localStorage.removeItem('token');
            localStorage.removeItem('adminRole');
            setSuccess('Account created! Redirecting to login...');
            // Perform a full reload to ensure app shows the login page (clears in-memory state)
            setTimeout(() => { window.location.href = '/'; }, 1200);
        } catch (err) {
            const msg = err.response?.data?.message ?? err.response?.data ?? err.message;
            setError(typeof msg === 'string' ? msg : 'Failed to accept invite.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component="main">
            <Grid container justifyContent="center" sx={{ mt: 12 }}>
                <Grid item xs={12} md={12} lg={10}>
                    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden', maxWidth: 840, mx: 'auto' }}>
                        <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
                            <Box
                                component="img"
                                src="ryslogo.png"
                                alt="Retool Your School"
                                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
                            />
                        </Box>
                        <Box sx={{ p: { xs: 4, md: 5 }, px: { xs: 8, md: 12 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, textAlign: 'center' }}>Admin Dashboard Accept Invite</Typography>
                            {invite && !error && (
                                <>
                                    <Typography sx={{ mb: 2, textAlign: 'center' }}>
                                        You are accepting an invite for <b>{invite.inviteeEmail}</b>
                                    </Typography>
                                </>
                            )}
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                            {!error && !success && (
                                <Box component="form" onSubmit={handleSubmit} noValidate>
                                    <Grid container spacing={2} direction="column">
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Password"
                                                name="password"
                                                type="password"
                                                value={form.password}
                                                onChange={handleChange}
                                                onBlur={() => handleBlur('password')}
                                                required
                                                error={touched.password && form.password.length < 8}
                                                helperText={touched.password && form.password.length < 8 ? 'Password must be at least 8 characters' : ''}
                                                InputProps={{ sx: { height: 56, fontSize: 18, width: 360 } }}
                                                sx={{ '& .MuiOutlinedInput-root': { fontSize: 18, height: 56, width: 360 }, mx: 'auto', display: 'block' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Confirm Password"
                                                name="confirmPassword"
                                                type="password"
                                                value={form.confirmPassword}
                                                onChange={handleChange}
                                                onBlur={() => handleBlur('confirmPassword')}
                                                required
                                                error={touched.confirmPassword && form.confirmPassword !== form.password}
                                                helperText={touched.confirmPassword && form.confirmPassword !== form.password ? 'Passwords do not match' : ''}
                                                InputProps={{ sx: { height: 56, fontSize: 18, width: 360 } }}
                                                sx={{ '& .MuiOutlinedInput-root': { fontSize: 18, height: 56, width: 360 }, mx: 'auto', display: 'block' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <PrimaryButton type="submit" disabled={!valid || submitting} sx={{ backgroundColor: '#ff6600', '&:hover': { backgroundColor: '#e65c00' }, fontSize: 18, height: 44, width: 200 }}>
                                                    Accept Invite
                                                </PrimaryButton>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AcceptInvite;
